import { useState, useRef, useEffect } from "react";
import { Send, Sparkles, User, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { useI18n, useL } from "../i18n";
import { streamChat } from "../api";
import { useSpeechRecognition, useSpeechSynthesis } from "../hooks/useVoice";

const SESSION_KEY = "rahhal.chat.session";

export default function AiAssistantChat() {
  const { t, lang } = useI18n();
  const L = useL();
  const [sessionId] = useState(() => {
    let sid = localStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  });
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [voiceOn, setVoiceOn] = useState(false);
  const scrollRef = useRef(null);

  const stt = useSpeechRecognition({
    lang,
    onFinal: (text) => { if (text.trim()) send(text); },
  });
  const tts = useSpeechSynthesis({ lang });

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, streaming]);

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: message }, { role: "assistant", content: "" }]);
    setStreaming(true);
    let full = "";
    try {
      for await (const chunk of streamChat({ session_id: sessionId, message, language: lang })) {
        full += chunk;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + chunk };
          return copy;
        });
      }
      if (voiceOn && tts.supported && full.trim()) tts.speak(full);
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: L({
          ar: "تعذّر الاتصال بالمساعد. حاول مجدداً.",
          en: "Assistant unavailable. Try again.",
          fr: "Assistant indisponible. Réessayez."
        }) };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const quickPrompts = [
    t("ai_quick_1"), t("ai_quick_2"), t("ai_quick_3"), t("ai_quick_4"),
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12" data-testid="ai-assistant">
      <div className="text-center mb-6">
        <div className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-[#FAF7F2] px-4 py-1.5 text-xs uppercase tracking-widest">
          <Sparkles className="h-3.5 w-3.5" />
          Gemini 3.1 Pro
        </div>
        <h1 className="mt-3 font-display text-3xl sm:text-4xl text-[#1C1917]">{t("ai_title")}</h1>
        <p className="mt-2 text-[#57534E]">{t("ai_sub")}</p>
      </div>

      <div className="rounded-3xl bg-white border border-[#E6DED3] overflow-hidden flex flex-col h-[62vh] min-h-[420px]">
        <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-scroll">
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center gap-4 py-10">
              <div className="h-16 w-16 rounded-2xl bg-[#F3EDE4] text-[#C85A32] grid place-items-center">
                <Sparkles className="h-8 w-8" />
              </div>
              <p className="text-[#57534E] max-w-sm">{t("ai_empty")}</p>
              <div className="flex flex-wrap gap-2 justify-center max-w-lg">
                {quickPrompts.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => send(q)}
                    data-testid={`quick-prompt-${i}`}
                    className="px-3 py-1.5 rounded-full bg-[#F3EDE4] hover:bg-[#E6DED3] text-sm text-[#1C1917] transition-colors"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            messages.map((msg, i) => <Bubble key={i} msg={msg} streaming={streaming && i === messages.length - 1} />)
          )}
        </div>

        <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="border-t border-[#E6DED3] p-3 flex items-center gap-2 bg-[#FAF7F2]"
        >
          {stt.supported && (
            <button
              type="button"
              onClick={() => (stt.listening ? stt.stop() : stt.start())}
              disabled={streaming}
              data-testid="voice-record"
              title={L({ ar: "تحدّث الآن", en: "Speak now", fr: "Parlez maintenant" })}
              className={`h-11 w-11 rounded-full grid place-items-center transition-colors border ${
                stt.listening ? "bg-[#C85A32] text-white border-[#C85A32] animate-pulse" : "bg-white text-[#1C1917] border-[#E6DED3] hover:border-[#1C1917]"
              }`}
            >
              {stt.listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
          )}
          {tts.supported && (
            <button
              type="button"
              onClick={() => { setVoiceOn((v) => !v); if (voiceOn) tts.stop(); }}
              data-testid="voice-toggle"
              title={L({ ar: "اقرأ الرد صوتياً", en: "Read reply aloud", fr: "Lire à voix haute" })}
              className={`h-11 w-11 rounded-full grid place-items-center transition-colors border ${
                voiceOn ? "bg-[#1C1917] text-[#FAF7F2] border-[#1C1917]" : "bg-white text-[#1C1917] border-[#E6DED3] hover:border-[#1C1917]"
              }`}
            >
              {voiceOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
            </button>
          )}
          <input
            value={stt.listening ? stt.transcript : input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={stt.listening ? L({ ar: "أستمع إليك…", en: "Listening…", fr: "J'écoute…" }) : t("ai_placeholder")}
            data-testid="chat-input"
            className="flex-1 h-11 px-4 rounded-full bg-white border border-[#E6DED3] focus:border-[#1C1917] outline-none text-sm"
            disabled={streaming || stt.listening}
          />
          <button
            type="submit"
            disabled={streaming || !input.trim()}
            data-testid="chat-send"
            className="h-11 w-11 rounded-full bg-[#1C1917] text-[#FAF7F2] hover:bg-[#C85A32] disabled:opacity-40 disabled:cursor-not-allowed grid place-items-center transition-colors"
          >
            <Send className="h-4 w-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

function Bubble({ msg, streaming }) {
  const isUser = msg.role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse rtl:flex-row" : "rtl:flex-row-reverse"}`} data-testid={`bubble-${msg.role}`}>
      <div className={`h-8 w-8 rounded-full grid place-items-center shrink-0 ${
        isUser ? "bg-[#1C1917] text-[#FAF7F2]" : "bg-[#F3EDE4] text-[#C85A32]"
      }`}>
        {isUser ? <User className="h-4 w-4" /> : <Sparkles className="h-4 w-4" />}
      </div>
      <div className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap leading-relaxed ${
        isUser ? "bg-[#1C1917] text-[#FAF7F2]" : "bg-[#F3EDE4] text-[#1C1917]"
      }`}>
        {msg.content}
        {streaming && !msg.content && (
          <span className="inline-flex gap-1">
            <span className="dot">●</span><span className="dot">●</span><span className="dot">●</span>
          </span>
        )}
      </div>
    </div>
  );
}
